import { Timestamp } from '@griply/types';
import { endOfQuarter, getQuarter } from 'date-fns';

export const NO_DEADLINE_QUARTER_KEY = 'no-deadline';

export type DeadlineQuarter = {
  year: number;
  quarter: number;
};

export const getDeadlineQuarterKey = (
  deadline: Timestamp | null | undefined,
): string => {
  if (!deadline) {
    return NO_DEADLINE_QUARTER_KEY;
  }
  return `${deadline.getFullYear()}-Q${getQuarter(deadline)}`;
};

export const parseDeadlineQuarterKey = (
  key: string,
): DeadlineQuarter | null => {
  const match = /^(\d{4})-Q([1-4])$/.exec(key);
  if (!match) {
    return null;
  }
  return { year: Number(match[1]), quarter: Number(match[2]) };
};

// Display label for a quarter key (e.g. '2026-Q1' → 'Q1 2026'). Returns null
// for the no-deadline bucket or any non-quarter key, so callers can substitute
// their own translated "no deadline" label.
export const formatDeadlineQuarterLabel = (key: string): string | null => {
  const parsed = parseDeadlineQuarterKey(key);
  if (!parsed) {
    return null;
  }
  return `Q${parsed.quarter} ${parsed.year}`;
};

// End of the quarter a key represents — used when a goal is dragged into a
// different quarter section and its deadline is rescheduled to match.
export const getDeadlineQuarterEndDate = (key: string): Timestamp | null => {
  const parsed = parseDeadlineQuarterKey(key);
  if (!parsed) {
    return null;
  }
  return endOfQuarter(new Date(parsed.year, (parsed.quarter - 1) * 3, 1));
};

// Chronological order, with the "no deadline" bucket always last.
export const compareDeadlineQuarterKeys = (a: string, b: string): number => {
  if (a === b) {
    return 0;
  }
  if (a === NO_DEADLINE_QUARTER_KEY) {
    return 1;
  }
  if (b === NO_DEADLINE_QUARTER_KEY) {
    return -1;
  }
  const parsedA = parseDeadlineQuarterKey(a);
  const parsedB = parseDeadlineQuarterKey(b);
  if (!parsedA || !parsedB) {
    return 0;
  }
  if (parsedA.year !== parsedB.year) {
    return parsedA.year - parsedB.year;
  }
  return parsedA.quarter - parsedB.quarter;
};
